import React from "react";
import { StudentProgressRow } from "./StudentProgressRow";
import { Student } from "./types";
import { useT } from "../../../../stores/uiLang";
import { TD_T } from "../../../../i18n/teacherDashboard";

interface StudentProgressTableProps {
  students: Student[];
  loading: boolean;
  classesLoading: boolean;
  classes: { id: string; name: string }[];
  studentsInitialized: boolean;
}

export function StudentProgressTable({
  students,
  loading,
  classesLoading,
  classes,
  studentsInitialized,
}: StudentProgressTableProps) {
  const t = useT(TD_T);
  if (loading || (classes.length > 0 && !studentsInitialized)) {
    return (
      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="relative shadow overflow-hidden border-b border-gray-200 sm:rounded-lg min-h-[350px]">
              <div className="flex items-center justify-center h-[350px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-500">{t.loadingStudents}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (classesLoading) {
    return (
      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="relative shadow overflow-hidden border-b border-gray-200 sm:rounded-lg min-h-[350px]">
              <div className="flex items-center justify-center h-[350px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                  <p className="text-sm text-gray-500">{t.loadingClasses}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (classes.length === 0) {
    return (
      <div className="flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="relative shadow overflow-hidden border-b border-gray-200 sm:rounded-lg min-h-[350px]">
              <div className="flex items-center justify-center h-[350px]">
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {t.noClassesFound}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {t.noClassesHelp}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
          <div className="relative shadow overflow-hidden border-b border-gray-200 sm:rounded-lg min-h-[350px]">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {t.studentName}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {t.classCol}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {t.performance}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {t.assignment}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {t.lastSubmitted}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {t.lastLogin}
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    {t.status}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {students.length > 0 &&
                  students.map((student) => (
                    <StudentProgressRow key={student.id} student={student} />
                  ))}
              </tbody>
              {students.length === 0 && (
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td colSpan={7} className="px-6 py-12">
                      <div className="flex items-center justify-center h-[250px]">
                        <div className="text-center">
                          <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {t.noStudentsFound}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {t.noStudentsHelp}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
